/// <amd-module name="TFS/Admin/OrganizationAadControls/Components/AadConnectPanel/AadConnectPanel" />
import * as React from "react";
import { filterXSS } from "xss";
import { Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { createMarkup } from "TFS/Admin/Common/Util/HtmlString";
import { AdminFeatureTelemetryLogger, AdminTelemetry } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { IAadConnectPanelComponentProps } from "TFS/Admin/OrganizationAadCommon/Contracts/ComponentControls.props";
import { ITenantData } from "TFS/Admin/OrganizationAadCommon/Contracts/DataModels";
import { OrganizationAadSource } from "TFS/Admin/OrganizationAadCommon/Sources/OrganizationAadSource";
import * as AdminResources from "TFS/Admin/OrganizationAadControls/Resources";
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IdentityRefWithEmail } from "VSS/Platform/Contracts/WebApi";
import { IVssFeatureService } from "VSS/Platform/Feature";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { useComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Panel } from "VSSUI/Panel";
import { PickListDropdown } from "VSSUI/PickList";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { VssPersona } from "VSSUI/VssPersona";

interface IAadConnectPanelComponentState {
    showTenantDetails: boolean;
    userSelectedTenant: ITenantData;
    hasSelectedTenant: boolean;
    getDisconnectUserListInProgress: boolean;
    disconnectUsers: IdentityRefWithEmail[];
    countOfUsersInOrg: number;
    showTooManyUsersWarning: boolean;
}

const TooManyDisconnectedUsersContactSupportFeatureFlag = "Azure.DevOps.AdminEngagement.OrganizationAad.TooManyDisconnectedUsersContactSupport";

function TooManyDisconnectedUsersWarningMessage(props: {
    organizationName: string;
    tenantDisplayName: string;
}) {
    const { pageContext } = useComponentContext();

    const showContactSupport = React.useMemo(() => {
        try {
            const featureService = pageContext.getService<IVssFeatureService>("IVssFeatureService");
            return featureService.isFeatureFlagEnabled(TooManyDisconnectedUsersContactSupportFeatureFlag);
        } catch {
            return false;
        }
    }, [pageContext]);

    return (
        <>
            <div className="fontWeightSemiBold">
                {showContactSupport
                    ? (
                        <FormatComponent format={AdminResources.WarningMessage_TooManyUsersContactSupport}>
                            <Link target="_blank" href={AdminResources.Link_ContactSupport}>{AdminResources.LinkText_ContactSupport}</Link>
                            <Link target="_blank" href={AdminResources.Link_ConnectToAadFaq}>{AdminResources.LinkText_LearnMore}</Link>
                        </FormatComponent>
                    )
                    : format(AdminResources.AadConnectionWarningWithTooManyUsers, props.organizationName)}
            </div>
            {!showContactSupport && (
                <p>
                    <span
                        dangerouslySetInnerHTML={createMarkup(
                            format(
                                AdminResources.AadConnectWarningMappingInfo,
                                filterXSS(props.tenantDisplayName)
                            )
                        )}
                    />
                </p>
            )}
        </>
    );
}

export class AadConnectPanelComponent extends React.Component<IAadConnectPanelComponentProps, IAadConnectPanelComponentState> {
    private hasUserTenant: boolean;
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.props.pageContext);

    public constructor(props: IAadConnectPanelComponentProps) {
        super(props);

        this.hasUserTenant = false;
        if (props.userTenantData && props.userTenantData.userTenants && props.userTenantData.userTenants.length > 0) {
            this.hasUserTenant = true;
        }

        this.state = {
            showTenantDetails: props.hasTenantConnected,
            userSelectedTenant: props.orgTenantData,
            hasSelectedTenant: false,
            getDisconnectUserListInProgress: false,
            disconnectUsers: [],
            countOfUsersInOrg: 0,
            showTooManyUsersWarning: false
        };
    }

    public render(): JSX.Element {
        const footerButtons: IButtonProps[] = [
            {
                onClick: this.props.onDismiss,
                text: AdminResources.Cancel
            },
            {
                className: "primary-action-button",
                onClick: () => {
                    this.props.onConnect(this.state.userSelectedTenant.id, this.state.userSelectedTenant.displayName);
                },
                primary: true,

                text: AdminResources.Connect,
                disabled: !this.hasUserTenant || !this.state.hasSelectedTenant || this.state.getDisconnectUserListInProgress
            }
        ];
        return (
            <Panel
                onDismiss={this.props.onDismiss}
                titleProps={{ text: AdminResources.AadConnectPanelTitle }}
                description={AdminResources.AadConnectPanelDescription}
                size={ContentSize.Large}
                footerButtonProps={footerButtons}
            >
                {this._renderPanelBody()}
            </Panel>
        );
    }

    private _renderPanelBody(): JSX.Element {
        return (
            <div className="width-100 aad-connect-panel-body">
                <div className="fontWeightSemiBold">{AdminResources.AadConnectPanelSignIn}</div>
                <div className="user-info-details flex-row flex-center">
                    <VssPersona
                        identityDetailsProvider={{
                            getIdentityImageUrl: (size: number): string | undefined => {
                                return this._getUserImage();
                            },
                            getDisplayName: (): string => {
                                return "";
                            }
                        }}
                        size={"large"}
                    />
                    <div className="left-margin">
                        <div>
                            <span className="fontWeightSemiBold">{this.props.userData.name}</span>
                        </div>
                        <div className="grey-font">{this.props.userData.email}</div>
                    </div>
                </div>
                <div className="aad-list-div fontWeightSemiBold">{AdminResources.AadConnectDirectory}</div>
                <div className="top-margin">
                    <PickListDropdown
                        getListItem={(item: ITenantData) => {
                            return {
                                key: item.id,
                                name: item.displayName
                            };
                        }}
                        getPickListItems={() => {
                            return this._getPickListItems();
                        }}
                        onSelectionChanged={this._onComboChanged}
                        placeholder={AdminResources.TenantDropDownPlaceHolder}
                        initiallySelectedItems={this.props.hasTenantConnected ? [this.props.orgTenantData] : []}
                        className="width-100"
                    />
                </div>

                {this.state.hasSelectedTenant === false ? (
                    <div className="small-top-margin grey-font aad-connect-panel-no-tenant-select">
                        <span className="small-top-margin connect-info-icon">
                            <Icon iconName="Info" />
                        </span>
                        <span className="small-top-margin">{AdminResources.TenantAskAdmin}</span>
                    </div>
                ) : null}

                {this.state.showTenantDetails === true ? this._renderTenantDetails() : null}

                {this.hasUserTenant === false ? this._renderBlockingMessage() : null}

                {this.state.getDisconnectUserListInProgress === true ? this._renderSpinner() : null}

                {this.state.hasSelectedTenant === true &&
                    ((this.state.disconnectUsers && this.state.disconnectUsers.length > 0) || this.state.showTooManyUsersWarning)
                    ? this._renderWarningMessage()
                    : null}
            </div>
        );
    }

    private _renderBlockingMessage(): JSX.Element {
        return (
            <div className="top-margin">
                <MessageBar severity={MessageBarSeverity.Error}>{format(AdminResources.NoUserTenantAvailable, this.props.userData.email)}</MessageBar>
            </div>
        );
    }

    private _renderSpinner(): JSX.Element {
        return <Spinner size={SpinnerSize.large} />;
    }

    private _renderWarningMessage(): JSX.Element {
        return (
            <div>
                <div className="big-top-margin">
                    <MessageBar severity={MessageBarSeverity.Warning}>
                        <div className="block-display">
                            {this.state.showTooManyUsersWarning
                                ? <TooManyDisconnectedUsersWarningMessage
                                    organizationName={this.props.organizationName}
                                    tenantDisplayName={this.state.userSelectedTenant.displayName}
                                />
                                : (
                                    <>
                                        <div className="fontWeightSemiBold">
                                            {format(AdminResources.AadConnectionWarning, this.props.organizationName)}
                                        </div>
                                        <p>
                                            <FormatComponent format={AdminResources.AadConnectionMemberLoseAccess}>
                                                <Link className="cursor-pointer" onClick={() => this.props.onOpenDisconnectUserDialog()}>
                                                    <span
                                                        dangerouslySetInnerHTML={createMarkup(
                                                            format(
                                                                AdminResources.AadConnectMemberLoseAccessLink,
                                                                this.state.disconnectUsers.length,
                                                                this.state.countOfUsersInOrg
                                                            )
                                                        )}
                                                    />
                                                </Link>
                                            </FormatComponent>
                                        </p>
                                        <p>
                                            <span
                                                dangerouslySetInnerHTML={createMarkup(
                                                    format(
                                                        AdminResources.AadConnectWarningMappingInfo,
                                                        filterXSS(this.state.userSelectedTenant.displayName)
                                                    )
                                                )}
                                            />
                                        </p>
                                    </>
                                )
                            }
                        </div>
                    </MessageBar>
                </div>
                <div className="top-margin">
                    <MessageBar severity={MessageBarSeverity.Info}>
                        <div className="block-display">
                            <span>{AdminResources.AadConnectionUserInfo}</span>
                            <span className="fontWeightSemiBold">{AdminResources.SSHKeyAndPAT}</span>
                            <span>
                                <Link href={AdminResources.ViewChecklistUrl} target="_blank">
                                    {AdminResources.ViewChecklistToInformUser}
                                </Link>
                            </span>
                        </div>
                    </MessageBar>
                </div>
            </div>
        );
    }

    private _renderTenantDetails(): JSX.Element {
        return (
            <div className="flex-row flex-center small-top-margin">
                <div className="aad-logo-div">
                    <Icon iconName="AzureLogo" className="aad-icon" />
                </div>
                <div>
                    <div className="small-top-margin fontWeightSemiBold">{this.state.userSelectedTenant.displayName}</div>
                    <div className="small-top-margin">{this.state.userSelectedTenant.domain}</div>
                    <div className="small-top-margin">{format(AdminResources.TenantId, this.state.userSelectedTenant.id)}</div>
                </div>
            </div>
        );
    }

    protected _onComboChanged = (option?: any, index?: number | undefined, value?: string | undefined, submitPendingValueEvent?: any) => {
        let tenantHasChanged = false;
        if (option.selectedItems && option.selectedItems[0] && option.selectedItems[0].id != this.state.userSelectedTenant.id) {
            tenantHasChanged = true;
        }
        const selectedOrgTenant = option.selectedItems[0].id != this.props.orgTenantData.id;
        this.setState({ userSelectedTenant: option.selectedItems[0], hasSelectedTenant: selectedOrgTenant, showTenantDetails: true });
        if (tenantHasChanged) {
            this._startActionCapture("Change User Tenant in Dropdown List", {});
            this._onGetListOfUsersInTenant(option.selectedItems[0].id);
        }
    };

    protected _onGetListOfUsersInTenant = async (newTenantId: string) => {
        this.setState({ getDisconnectUserListInProgress: true });
        const actionTelemetryLogger = this._startActionCapture("Get Disconnect User List", {});
        const source = new OrganizationAadSource();
        await source.getDisconnectedUsers(newTenantId, this.props.pageContext).then(
            result => {
                if (result) {
                    this.setState({
                        getDisconnectUserListInProgress: false,
                        disconnectUsers: result.users,
                        countOfUsersInOrg: result.orgUserCount,
                        showTooManyUsersWarning: result.showTooManyUsersWarning
                    });
                    this.props.onUpdateUnresolvedUsers(result);
                } else {
                    actionTelemetryLogger.captureError("No result returned from getDisconnectedUsers");
                    this.setState({ getDisconnectUserListInProgress: false });
                }
            },
            reason => {
                actionTelemetryLogger.captureError(reason.message);
                this.setState({ getDisconnectUserListInProgress: false });
            }
        );
    };

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.OrganizationAad, Feature.OrganizationAad, action, properties);
    };

    private _getPickListItems(): ITenantData[] {
        return this.props.userTenantData.userTenants;
    }

    private _getUserImage() {
        const url = getIdentityImageUrl(this.props.pageContext, this.props.userData.id);
        return url;
    }
}

VssComponent.register("ms.vss-admin-web.aad-connect-panel", AadConnectPanelComponent);
